import { CellData } from '@ddt-web/data-service';

import { TableHeaderType } from '../enums';

export interface ExpandedRowDetails {
	holdingCategoryName: string;
	holdingRowIndex: number;
	holdingRowCells: Array<CellData>;
}

export interface ScrollableHoldingsTableConfig {
	dataTabId?: string;
	header?: ScrollableHoldingsTableHeaderConfig; // provide if you need table headers from the component
	styleAttributes?: {
		leftTable?: { [attribute: string]: string };
	};
	options?: ScrollableHoldingsTableOptionsConfig;
	rowExpansionConfig?: RowExpansionConfig;
}

interface ScrollableHoldingsTableOptionsConfig {
	showPercentageSymbol?: boolean; // show values as percentages
	decimalPipeDigitsInfo?: string; // decimal pipe config
	rightColClass?: string; // used to override the default rightColClass
	leftColClass?: string; // used to override the default leftColClass
	componentBreakpoint?: number; // used to set componentBreakpoint in the table
	replaceZeroWithDash?: boolean; // replace zero value in categoryWeightMap with a `-` in the template
	showProductPageLinks?: boolean;
}

interface ScrollableHoldingsTableHeaderConfig {
	leftTableHeaderConfigs?: LeftTableHeaderConfigs;
	usePortfolioNameHeaders?: boolean; // use portfolio names for table headers on the right hand table
	rightTableHeaderConfigs?: Array<TableHeader | IconTableHeader>; // if !usePortfolioNameHeaders, provide an alternative here e.g. PC Dashboard
}

interface TableHeader {
	type: TableHeaderType;
	label: string; // the table heading
	widthPct: number; // the width percent for this heading that is applied to the th element. You must use 'table-layout': 'fixed' to enforce this
	dcrHTMLKey?: string; // dcr override for the label
}

export interface AdditionalTableHeader extends TableHeader {
	dataIndex?: number; // where to look when pulling from the cells -> tableData.cells[assetIndex][heading.dataIndex].formattedValue
}
export interface IconTableHeader extends TableHeader {
	icons: Array<IconConfig>; // icons that can be displayed in the icon column
}

export interface IconConfig {
	iconType: string; // icon directive type
	cssClasses: string; // css class name
	// applicableHoldingsIndices: Array<number>; // indices of holdings where the icon should be shown
	constraint: (category, assetIndex) => boolean; // indices of holdings where the icon should be shown
	clickHandler?: Function;
	tooltipDcr?: string;
}

export interface RowExpansionConfig {
	// These are marked as optional but should be set in AfterViewInit
	leftTemplate?: any;
	rightTemplate?: any;
}

type DefaultLeftTableHeaderConfig = [HoldingIdentifierTableHeader, HoldingDescriptionTableHeader, ...Array<AdditionalTableHeader | IconTableHeader>];
type IconFirstLeftTableHeaderConfig = [IconTableHeader, HoldingIdentifierTableHeader, HoldingDescriptionTableHeader, ...Array<AdditionalTableHeader | IconTableHeader>];

type LeftTableHeaderConfigs = DefaultLeftTableHeaderConfig | IconFirstLeftTableHeaderConfig;

export type HoldingIdentifierTableHeader = TableHeader;
export type HoldingDescriptionTableHeader = AdditionalTableHeader;


// Type guards
export const isHoldingIdentifierTableHeader = (header: TableHeader): header is HoldingIdentifierTableHeader => {
	return header.type === TableHeaderType.ID;
};

export const isHoldingDescriptionTableHeader = (header: TableHeader): header is HoldingDescriptionTableHeader => {
	return header.type === TableHeaderType.Description;
};

export const isIconTableHeader = (header: TableHeader): header is IconTableHeader => {
	return header.type === TableHeaderType.Icon;
};

export const isIconFirstLeftTableHeaderConfig = (headers: LeftTableHeaderConfigs): headers is IconFirstLeftTableHeaderConfig => {
	return isIconTableHeader(headers[0]);
};
